import React from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import FragmentContent from './FragmentContent';
import { FRAGMENT_STATUS } from '../features/fragmentsSlice';

// Viewport-relative IntersectionObserver root: the mega menu is a fixed overlay,
// not a scroll container, so its fragments observe against the viewport (null root).
const VIEWPORT_ROOT = null;

const FRAGMENT_CLASS_MAP = {
	'hours-default': ['py-4', 'ddc-font-size-small']
};

const resolveExtraClasses = (html) => {
	if (!html) return [];
	return Object.entries(FRAGMENT_CLASS_MAP)
		.filter(([detectClass]) => html.includes(detectClass))
		.flatMap(([, classes]) => classes);
};

/**
 * Wrapper-specific class-list policy for mega menu fragments.
 *
 * @param {{ status: string, html: string|null }} state
 * @returns {string}
 */
const resolveClassName = ({ status, html }) => {
	const isLoading =
		status === FRAGMENT_STATUS.IDLE || status === FRAGMENT_STATUS.LOADING;
	const extraClasses =
		status === FRAGMENT_STATUS.LOADED ? resolveExtraClasses(html) : [];
	return setClassNames([
		'ws-nav-drawer__fragment',
		...extraClasses,
		isLoading && 'ws-nav-drawer__fragment--loading'
	]);
};

/**
 * MegaMenuFragment — thin wrapper over FragmentContent that supplies the mega
 * menu's viewport-relative observer root and its own class-list policy.
 *
 * @param {{ url?: string, skeletonHeight?: string }} props
 */
const MegaMenuFragment = ({ url, skeletonHeight = '12rem' }) => (
	<FragmentContent
		url={url}
		root={VIEWPORT_ROOT}
		classNameResolver={resolveClassName}
		skeletonHeight={skeletonHeight}
		// Mega menu stays mounted for the page's life and only toggles with
		// display:none — re-initializing on every reopen would leak DOM nodes +
		// listeners, so opt out and initialize once.
		reinitOnReshow={false}
	/>
);

MegaMenuFragment.propTypes = {
	url: PropTypes.string,
	skeletonHeight: PropTypes.string
};

export default MegaMenuFragment;
